import { useCallback } from "react";
import { getSceneState } from "../../../stores/sceneStore";
import { useSelectionStore } from "../../../stores/selectionStore";
import {
  addDefaultBody,
  addDefaultJoint,
  addDefaultSite,
  addDefaultCollisionGeom,
  addDefaultVisualGeom,
} from "../../../utils/bodyAddActions";
import {
  addDefaultBodyConstraint,
  addDefaultJointConstraint,
  addDefaultTendon,
} from "../../../utils/constraintAddActions";
import {
  runDuplicateBodySubtreeGrouped,
  duplicateCollisionGeomFromKey,
  collisionGeomSelectionKeyAfterDuplicate,
  activateMoveGizmoForDuplicatedBody,
} from "../../../utils/sceneDuplicateCommands";
import { focusNewEntityInInspector } from "../../../utils/focusNewEntityInInspector";
import { rememberAutoParentForSelectedItem } from "../../../utils/pendingAutoParentDeletion";
import { nextAvailableCameraName } from "../../../utils/nextAvailableCameraName";
import { dispatchViewportCameraVisibility } from "../../draggable-panels/scene-inventory/sceneInventoryViewportEvents";

export function useAddDuplicateActions({
  isPhysicsRunning,
  bodies,
  joints,
  equalities,
  tendons,
  cameras,
  worldRootTargetBodyUuid,
  sceneActions,
  closeContextMenu,
}) {
  const handleAddChildBody = useCallback(
    (parentBodyUuid) => {
      if (isPhysicsRunning || !parentBodyUuid) return;
      const addedBodyUuid = addDefaultBody(sceneActions, parentBodyUuid);
      if (addedBodyUuid) {
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectBody(addedBodyUuid)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, sceneActions, closeContextMenu]
  );

  const handleAddRootBody = useCallback(() => {
    if (isPhysicsRunning) return;
    const addedBodyUuid = addDefaultBody(sceneActions, null);
    if (addedBodyUuid) {
      focusNewEntityInInspector("sceneTree", () =>
        useSelectionStore.getState().selectBody(addedBodyUuid)
      );
    }
    closeContextMenu();
  }, [isPhysicsRunning, sceneActions, closeContextMenu]);

  const handleDuplicateBody = useCallback(
    (sourceBodyUuid, includeBranch = false) => {
      if (isPhysicsRunning || !sourceBodyUuid) return;
      const sourceBody = (bodies || {})[sourceBodyUuid];
      if (!sourceBody) return;
      const newUuid = runDuplicateBodySubtreeGrouped(sceneActions, sourceBodyUuid, includeBranch);
      if (newUuid) {
        focusNewEntityInInspector(
          "sceneTree",
          () => useSelectionStore.getState().selectBody(newUuid),
          { duplicateChromeFlash: true }
        );
        activateMoveGizmoForDuplicatedBody(newUuid);
      }
      closeContextMenu();
    },
    [isPhysicsRunning, bodies, sceneActions, closeContextMenu]
  );

  const handleDuplicateCollision = useCallback(
    (geomKey) => {
      if (isPhysicsRunning || !geomKey) return;
      const newUuid = duplicateCollisionGeomFromKey(sceneActions, bodies, geomKey);
      const selectionKey = collisionGeomSelectionKeyAfterDuplicate(geomKey, newUuid);
      if (selectionKey) {
        focusNewEntityInInspector(
          "sceneTree",
          () => useSelectionStore.getState().selectGeom(selectionKey),
          { duplicateChromeFlash: true }
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, bodies, sceneActions, closeContextMenu]
  );

  const handleAddJointToBody = useCallback(
    (parentBodyUuid, kind, options = {}) => {
      if (isPhysicsRunning || !kind) return;
      const forceNewRoot = options.forceNewRoot === true;
      let parent = parentBodyUuid || null;
      let createdAutoParent = false;
      if (!parent) {
        if (!forceNewRoot && worldRootTargetBodyUuid) {
          parent = worldRootTargetBodyUuid;
        } else {
          parent = addDefaultBody(sceneActions, null, [0, 0, 0], {
            addFreeJoint: kind === "free",
          });
          createdAutoParent = Boolean(parent);
        }
      }
      if (!parent) return;
      if (createdAutoParent && kind === "free") {
        const addedJointUuid = getSceneState()?.bodies?.[parent]?.joints?.[0] || null;
        if (addedJointUuid) {
          focusNewEntityInInspector("sceneTreeObject", () =>
            useSelectionStore.getState().selectBody(parent)
          );
        }
        closeContextMenu();
        return;
      }
      const addedJointUuid = addDefaultJoint(sceneActions, parent, kind);
      if (addedJointUuid) {
        if (createdAutoParent) {
          rememberAutoParentForSelectedItem({
            itemType: "joint",
            itemUuid: addedJointUuid,
            parentBodyUuid: parent,
          });
        }
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectJoint(addedJointUuid)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, sceneActions, closeContextMenu, worldRootTargetBodyUuid]
  );

  const handleAddSiteToBody = useCallback(
    (parentBodyUuid) => {
      if (isPhysicsRunning) return;
      const addedSiteUuid = addDefaultSite(sceneActions, parentBodyUuid ?? null);
      if (addedSiteUuid) {
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectSite(addedSiteUuid)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, sceneActions, closeContextMenu]
  );

  const handleAddGeomToBody = useCallback(
    (parentBodyUuid, kind, options = {}) => {
      if (isPhysicsRunning || !kind) return;
      const forceNewRoot = options.forceNewRoot === true;
      let parent = parentBodyUuid || null;
      let createdAutoParent = false;
      if (!parent) {
        if (!forceNewRoot && worldRootTargetBodyUuid) {
          parent = worldRootTargetBodyUuid;
        } else {
          parent = addDefaultBody(sceneActions, null);
          createdAutoParent = Boolean(parent);
        }
      }
      if (!parent) return;
      const addedGeomUuid = addDefaultCollisionGeom(sceneActions, parent, kind);
      if (addedGeomUuid) {
        if (createdAutoParent) {
          rememberAutoParentForSelectedItem({
            itemType: "geom",
            itemUuid: addedGeomUuid,
            parentBodyUuid: parent,
          });
        }
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectGeom(`${parent}:${addedGeomUuid}`)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, sceneActions, closeContextMenu, worldRootTargetBodyUuid]
  );

  const handleAddVisualGeomToBody = useCallback(
    (parentBodyUuid, kind, options = {}) => {
      if (isPhysicsRunning || !kind) return;
      const forceNewRoot = options.forceNewRoot === true;
      let parent = parentBodyUuid || null;
      let createdAutoParent = false;
      if (!parent) {
        if (!forceNewRoot && worldRootTargetBodyUuid) {
          parent = worldRootTargetBodyUuid;
        } else {
          parent = addDefaultBody(sceneActions, null);
          createdAutoParent = Boolean(parent);
        }
      }
      if (!parent) return;
      if (kind === "mesh") {
        window.dispatchEvent(
          new CustomEvent("start-visual-mesh-creation", {
            detail: { parentBodyUuid: parent },
          })
        );
        closeContextMenu();
        return;
      }
      const addedGeomUuid = addDefaultVisualGeom(sceneActions, parent, kind);
      if (addedGeomUuid) {
        if (createdAutoParent) {
          rememberAutoParentForSelectedItem({
            itemType: "geom",
            itemUuid: addedGeomUuid,
            parentBodyUuid: parent,
          });
        }
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectGeom(`${parent}:${addedGeomUuid}`)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, sceneActions, closeContextMenu, worldRootTargetBodyUuid]
  );

  const handleAddActuatorForJoint = useCallback(
    (jointUuid) => {
      if (isPhysicsRunning || !jointUuid) return;
      const jointData = joints?.[jointUuid];
      const defaultCtrlrange =
        Array.isArray(jointData?.range) && jointData.range.length === 2
          ? [jointData.range[0], jointData.range[1]]
          : [-1, 1];
      sceneActions.addActuator({
        name: `${jointData?.name ?? "joint"}_actuator`,
        kind: "motor",
        joint: jointUuid,
        ctrlrange: defaultCtrlrange,
        gear: 1,
      });
      closeContextMenu();
    },
    [isPhysicsRunning, joints, sceneActions, closeContextMenu]
  );

  const handleAddCameraToBody = useCallback(
    (parentBodyUuid) => {
      if (isPhysicsRunning || !parentBodyUuid) return;
      const addedCameraUuid = sceneActions.addCamera({
        name: nextAvailableCameraName(cameras, {
          kind: "mounted_body",
          bodyName: bodies?.[parentBodyUuid]?.name || String(parentBodyUuid),
        }),
        kind: "mounted",
        position: [0, 0, 0.5],
        orientation: [1, 0, 0, 0],
        fovy: 65,
        width: 800,
        height: 800,
        fps: 8,
        mountType: "body",
        mountBodyUuid: parentBodyUuid,
      });
      if (addedCameraUuid) {
        dispatchViewportCameraVisibility(true);
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectCamera(addedCameraUuid)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, sceneActions, cameras, bodies, closeContextMenu]
  );

  const handleAddFixedWorldCamera = useCallback(() => {
    if (isPhysicsRunning) return;
    const addedCameraUuid = sceneActions.addCamera({
      name: nextAvailableCameraName(cameras, { kind: "world" }),
      kind: "fixed",
      position: [0, 0.5, 2],
      orientation: [1, 0, 0, 0],
      fovy: 65,
      width: 800,
      height: 800,
      fps: 8,
    });
    if (addedCameraUuid) {
      dispatchViewportCameraVisibility(true);
      focusNewEntityInInspector("sceneTree", () =>
        useSelectionStore.getState().selectCamera(addedCameraUuid)
      );
    }
    closeContextMenu();
  }, [isPhysicsRunning, sceneActions, cameras, closeContextMenu]);

  const handleAddConstraintForBody = useCallback(
    (bodyUuid) => {
      if (isPhysicsRunning) return;
      const addedEqualityUuid = addDefaultBodyConstraint(
        sceneActions,
        bodies || {},
        bodyUuid,
        "connect",
        equalities || {}
      );
      if (addedEqualityUuid) {
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectEquality(addedEqualityUuid)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, bodies, equalities, sceneActions, closeContextMenu]
  );

  const handleAddConstraintForJoint = useCallback(
    (jointUuid) => {
      if (isPhysicsRunning || !jointUuid) return;
      const addedEqualityUuid = addDefaultJointConstraint(
        sceneActions,
        joints || {},
        jointUuid,
        equalities || {}
      );
      if (addedEqualityUuid) {
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectEquality(addedEqualityUuid)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, joints, equalities, sceneActions, closeContextMenu]
  );

  const handleAddTendonForJoint = useCallback(
    (jointUuid) => {
      if (isPhysicsRunning || !jointUuid) return;
      const addedTendonUuid = addDefaultTendon(
        sceneActions,
        joints || {},
        jointUuid,
        tendons || {}
      );
      if (addedTendonUuid) {
        focusNewEntityInInspector("sceneTree", () =>
          useSelectionStore.getState().selectTendon(addedTendonUuid)
        );
      }
      closeContextMenu();
    },
    [isPhysicsRunning, joints, tendons, sceneActions, closeContextMenu]
  );

  return {
    handleAddChildBody,
    handleAddRootBody,
    handleDuplicateBody,
    handleDuplicateCollision,
    handleAddJointToBody,
    handleAddSiteToBody,
    handleAddGeomToBody,
    handleAddVisualGeomToBody,
    handleAddActuatorForJoint,
    handleAddCameraToBody,
    handleAddFixedWorldCamera,
    handleAddConstraintForBody,
    handleAddConstraintForJoint,
    handleAddTendonForJoint,
  };
}
